'use client';

/**
 * Container for the AI assistant chat.
 *
 * - Desktop (md+): in-flow right-side column inside the layout flex row, so it
 *   PUSHES the page content instead of overlaying it. `relative z-[150]` keeps
 *   the panel (and its working animations) visible above modal backdrops
 *   (z-[100]) while the AI fills a dialog — but below z-[200] confirmations.
 * - Mobile (<md): fixed bottom sheet (Blackbook pattern — full width, rounded
 *   top corners, drag handle, max 65vh) above the bottom navigation.
 *
 * One single element switches between the two via responsive classes so the
 * chat state (useChat inside AiChat) is preserved across breakpoints.
 */

import { useRef } from 'react';
import { useAiAssistant } from '@/lib/platform/contexts/ai-assistant-context';
import { useOrganization } from '@/lib/platform/contexts/organization-context';
import { XMarkIcon, SparklesIcon } from '@heroicons/react/24/outline';
import AiChat from './AiChat';
import AiWorkingStar from './AiWorkingStar';

/** Snap/close animation duration for the mobile drag-to-dismiss (ms) */
const SHEET_ANIMATION_MS = 250;
/** Drag past this fraction of the sheet height closes it */
const DISMISS_DISTANCE_FRACTION = 0.3;
/** Downward flick faster than this (px/ms) closes regardless of distance */
const DISMISS_VELOCITY = 0.5;

export default function AiAssistantPanel() {
  const { isOpen, close, working } = useAiAssistant();
  const { organization } = useOrganization();

  // --- Mobile drag-to-dismiss (iOS sheet behavior) -------------------------
  // The sheet follows the finger 1:1 downward (rubber-band resistance upward);
  // on release it closes when dragged past 30% of its height or flicked down
  // fast, otherwise it springs back. Transform is applied directly on the DOM
  // node (no re-render per touchmove). Desktop (md+, in-flow side column) is
  // untouched — handlers bail out via the media query check.
  const panelRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    startY: number;
    lastY: number;
    lastT: number;
    velocity: number;
    height: number;
  } | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (window.matchMedia('(min-width: 768px)').matches) return; // desktop panel: no drag
    const panel = panelRef.current;
    if (!panel) return;
    const touch = e.touches[0];
    dragRef.current = {
      startY: touch.clientY,
      lastY: touch.clientY,
      lastT: e.timeStamp,
      velocity: 0,
      height: panel.offsetHeight,
    };
    panel.style.transition = 'none'; // 1:1 finger tracking
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const drag = dragRef.current;
    const panel = panelRef.current;
    if (!drag || !panel) return;
    const touch = e.touches[0];
    const dt = e.timeStamp - drag.lastT;
    if (dt > 0) drag.velocity = (touch.clientY - drag.lastY) / dt;
    drag.lastY = touch.clientY;
    drag.lastT = e.timeStamp;
    const dy = touch.clientY - drag.startY;
    const offset = dy >= 0 ? dy : dy / 3; // rubber-band resistance upward
    panel.style.transform = `translateY(${offset}px)`;
  };

  const handleTouchEnd = () => {
    const drag = dragRef.current;
    const panel = panelRef.current;
    dragRef.current = null;
    if (!drag || !panel) return;
    const dy = drag.lastY - drag.startY;
    const shouldClose =
      dy > drag.height * DISMISS_DISTANCE_FRACTION || (dy > 0 && drag.velocity > DISMISS_VELOCITY);
    panel.style.transition = `transform ${SHEET_ANIMATION_MS}ms ease-out`;
    panel.style.transform = shouldClose ? 'translateY(100%)' : 'translateY(0)';
    window.setTimeout(() => {
      if (shouldClose) close(); // panel goes display:none (hidden) — reset is invisible
      panel.style.transition = '';
      panel.style.transform = '';
    }, SHEET_ANIMATION_MS);
  };

  // Stay mounted while closed (hidden) so the conversation survives close/reopen
  return (
    <div
      ref={panelRef}
      className={`${isOpen ? 'flex' : 'hidden'} fixed inset-x-0 bottom-0 z-[150] h-[65vh] flex-col rounded-t-[20px] border-t border-gray-200 bg-white shadow-2xl md:relative md:h-auto md:w-72 md:shrink-0 md:rounded-none md:border-t-0 md:border-l md:shadow-none dark:border-gray-700 dark:bg-gray-900 print:hidden`}
    >
      {/* Drag area: handle + header (NOT the chat content — that scrolls).
          touch-none stops the browser from scrolling while dragging the sheet;
          desktop keeps default touch behavior. */}
      <div
        className="shrink-0 touch-none md:touch-auto"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        {/* Mobile drag handle */}
        <div className="flex shrink-0 justify-center pt-2 md:hidden">
          <div className="h-1 w-10 rounded-full bg-gray-300 dark:bg-gray-600" />
        </div>

        {/* Header */}
        {/* md:h-16 matches the app header height so the bottom borders align */}
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-4 py-3 md:h-16 md:py-0 dark:border-gray-700">
          <div className="flex items-center gap-2">
            {/* Conditional render mounts the star sequence WITH its animation
              class — Safari doesn't reliably start a CSS animation when the
              class is added to an existing element */}
            {working ? (
              <AiWorkingStar className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            ) : (
              <SparklesIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            )}
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Valiyou AI
            </span>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close AI assistant"
            className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Chat — keyed by organization: a conversation is locked to ONE
          organization. Switching org (manual, via the app's org switcher —
          the AI can never switch) starts a fresh conversation. */}
      <div className="min-h-0 flex-1">
        <AiChat key={organization?.id ?? 'no-org'} />
      </div>
    </div>
  );
}
